import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Notify } from "@/components/ui/notify";
import { useInterpretations, useInterpretation } from "@/features/interpretations/hooks";
import { InterpretationBody } from "@/features/interpretations/InterpretationBody";
import { HistoryList } from "./HistoryList";

// Página deep-linkável da interpretação. Dois acessos: por simulação
// (/simulations/:simulationId/interpretations/:id?) com histórico à esquerda; ou standalone
// (/interpretations/:interpretationId, ex.: link do dock) que deriva a simulação dos fatos.

export function SimulationInterpretationPage(): React.ReactElement {
  const params = useParams();
  const navigate = useNavigate();
  const scoped = !!params.simulationId;
  const history = useInterpretations(params.simulationId ?? "", scoped);
  const selectedId = params.interpretationId ?? history.data?.[0]?.id;
  const detail = useInterpretation(selectedId);
  const simulationId = params.simulationId ?? detail.data?.facts.target.simulationId ?? "";

  const back = () => navigate(simulationId ? `/simulations/${simulationId}` : "/simulations");
  const bodyPanel = (
    <Card>
      <CardContent className="p-0">
        {detail.isError ? (
          <div className="p-5">
            <Notify.QueryError error={detail.error} onRetry={() => void detail.refetch()} />
          </div>
        ) : detail.isLoading || !detail.data ? (
          <p className="p-5 text-sm text-muted-foreground">Carregando…</p>
        ) : (
          <InterpretationBody data={detail.data} />
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-4 flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={back}>
          <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden />
          {simulationId ? "Voltar à simulação" : "Voltar"}
        </Button>
        <h1 className="text-[15px] font-semibold tracking-tight">Interpretação</h1>
      </div>

      {scoped && history.isError ? (
        <Notify.QueryError
          error={history.error}
          onRetry={() => void history.refetch()}
          title="Não foi possível carregar as interpretações"
        />
      ) : scoped && history.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : scoped && (history.data?.length ?? 0) === 0 ? (
        <Notify.Empty
          title="Nenhuma interpretação ainda"
          description="Gere uma interpretação pela tela de resultado da simulação."
          action={
            <Button variant="outline" size="sm" onClick={back}>
              Ir para a simulação
            </Button>
          }
        />
      ) : scoped ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_1fr]">
          <aside>
            <HistoryList
              items={history.data ?? []}
              selectedId={selectedId}
              onSelect={(id) =>
                navigate(`/simulations/${simulationId}/interpretations/${id}`, { replace: true })
              }
            />
          </aside>
          {bodyPanel}
        </div>
      ) : (
        bodyPanel
      )}
    </div>
  );
}
